import { useEffect } from 'react';
import { href } from '../app/router';
import { GreekText } from '../components/GreekText';
import { Illustration } from '../components/Illustration';
import { PlayButton } from '../components/PlayButton';
import { Status, StatusShape } from '../components/Status';
import { course, staffOf, trackFor, unitOf } from '../content';
import { pathOf } from '../lib/plan';
import { candoState, lessonStatus, phraseStatus, tally } from '../lib/progress';
import { clipFor, itemVoice, speechRange } from '../lib/voice';
import { useAppState } from '../state/store';
import { InlineGreek } from '../components/InlineGreek';

/** The situation's can-dos, each with its phrases and how well they're known; then the patterns. */
export function Phrases({ lesson: focus }: { lesson?: string }) {
  const state = useAppState();
  useEffect(() => {
    if (focus) document.getElementById(`lesson-${focus}`)?.scrollIntoView({ block: 'start' });
  }, [focus]);
  const track = trackFor(state.profile.goal);
  // The learner's own path: their track's lessons, in its order.
  const lessons = pathOf(state, course);
  const pathIds = lessons.flatMap((l) => l.phrases);
  const t = tally(state, course, pathIds);
  const total = pathIds.length;
  const met = total - t.unmet;
  const solid = t.known + t.mastered;
  const ladder = [
    { status: 'known' as const, label: 'Solid', n: solid },
    { status: 'learning' as const, label: 'Settling in', n: t.learning },
    { status: 'unmet' as const, label: 'New', n: t.unmet },
  ];

  return (
    <main className="page" id="main">
      <header className="stack">
        <p className="eyebrow">Phrasebook · {track.label}</p>
        <h1 className="title">Your phrases</h1>
        <p className="subtitle">
          {met === 0 ? `${total} phrases to learn.` : `${met} of ${total} phrases met.`} Solid means you remember it well enough that its next
          review is a week or more away.
        </p>
        <a href={href('/reading')} className="text-link">
          How to read the Greek letters
        </a>
      </header>

      <div className="stack">
        <div className="meter" role="img" aria-label={`${solid} solid, ${t.learning} settling in, ${t.unmet} new, out of ${total}`}>
          {solid > 0 && <span className="meter-solid" style={{ width: `${(solid / total) * 100}%` }} />}
          {t.learning > 0 && <span className="meter-settling" style={{ width: `${(t.learning / total) * 100}%` }} />}
        </div>
        <div className="legend" aria-hidden="true">
          {ladder.map((step) => (
            <span key={step.label} className="legend-step" data-status={step.status}>
              <StatusShape status={step.status} />
              {step.label} {step.n}
            </span>
          ))}
        </div>
      </div>

      {lessons.map((lesson, i) => {
        const status = lessonStatus(state, lesson, course);
        return (
          <section key={lesson.id} id={`lesson-${lesson.id}`} className="stack" aria-labelledby={`l-${lesson.id}`} style={{ scrollMarginTop: 96 }}>
            <div className="lesson-head">
              <span className="path-thumb" aria-hidden="true">
                <Illustration id={lesson.id} size="thumb" decorative />
              </span>
              <div className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
                <p className="eyebrow">{unitOf(track, lesson.id)?.unit.title ?? course.situation.name} · lesson {i + 1}</p>
                <h2 className="section-title" id={`l-${lesson.id}`}>
                  {lesson.title}
                </h2>
                <p className="small muted">
                  <InlineGreek text={lesson.cando} />
                </p>
              </div>
              {status === 'done' && (candoState(state, lesson, course) === 'solid' ? <span className="chip chip-solid">Solid</span> : <span className="chip">Settling in</span>)}
              {status === 'current' && <span className="chip chip-next">Up next</span>}
            </div>
            <ul className="phrase-list">
              {lesson.phrases.map((id) => {
                const item = course.items.find((x) => x.id === id)!;
                const clip = clipFor(item, itemVoice(item, staffOf(item)));
                const range = speechRange(clip);
                return (
                  <li key={id} className="phrase-row">
                    <a className="phrase-link" href={href(`/phrase/${id}`)}>
                      <GreekText text={item.text} size="body" />
                      <span className="phrase-sub">
                        <span className="small muted">{item.meaning}</span>
                        <Status status={phraseStatus(state, id, item.role)} />
                      </span>
                    </a>
                    <div className="phrase-row-end">
                      <PlayButton src={clip.src} tag={`list:${id}`} label="Play" ariaLabel={`Play ${item.meaning}`} from={range.from} to={range.to} variant="icon" />
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}

      <section className="stack" aria-labelledby="patterns-h">
        <div className="stack" style={{ gap: 2 }}>
          <h2 className="section-title" id="patterns-h">
            Patterns
          </h2>
          <p className="small muted">Frames with a slot. Learn one, and you can say many things.</p>
        </div>
        {course.frames.map((frame) => {
          const members = course.items.filter((i) => i.slot?.frame === frame.id && pathIds.includes(i.id));
          if (members.length === 0) return null;
          return (
            <div key={frame.id} className="panel stack">
              <p>
                <span className="greek greek-large" lang="el">
                  {frame.pattern}
                </span>
              </p>
              <p className="muted">{frame.meaning}</p>
              <p className="small">
                <InlineGreek text={frame.note} />
              </p>
              <ul className="stack" style={{ listStyle: 'none', padding: 0, margin: 0, gap: 6 }}>
                {members.map((m) => (
                  <li key={m.id}>
                    <a href={href(`/phrase/${m.id}`)} style={{ color: 'inherit', textDecoration: 'none' }}>
                      <GreekText text={m.text} size="body" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>
    </main>
  );
}
